import { forwardRef } from "react";
import { HelperText, Select } from "@hireart/component-library";
import { useField } from "formik";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";

/**
 * FormSelect wires up Select with Formik
 */
export const FormSelect = forwardRef(
  (
    {
      "aria-describedby": ariaDescribedBy = undefined,
      className = "",
      id = undefined,
      isDisabled = false,
      isLabelUnstyled = false,
      isOptional = false,
      label = undefined,
      name,
      onChange = () => {},
      options,
      selectClassName = "",
      size = undefined,
    },
    ref
  ) => {
    const [field, meta] = useField(name);
    const hasError = meta.touched && !isEmpty(meta.error);
    const selectId = id || name;
    const helperTextIds = [
      hasError ? `${selectId}-error` : undefined,
      ariaDescribedBy,
    ]
      .filter(Boolean)
      .join(" ");

    const handleChange = (e, value) => {
      field.onChange({ target: { value, name } });
      onChange(e, value);
    };

    return (
      <div className={className}>
        <Select
          {...field}
          ref={ref}
          aria-describedby={isEmpty(helperTextIds) ? undefined : helperTextIds}
          className={selectClassName}
          hasError={hasError}
          id={selectId}
          isDisabled={isDisabled}
          isLabelUnstyled={isLabelUnstyled}
          isOptional={isOptional}
          label={label}
          onChange={handleChange}
          options={options}
          size={size}
        />
        {hasError && (
          <HelperText id={`${selectId}-error`} hasError>
            {meta.error}
          </HelperText>
        )}
      </div>
    );
  }
);

FormSelect.displayName = "FormSelect";

FormSelect.propTypes = {
  "aria-describedby": PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  isLabelUnstyled: PropTypes.bool,
  isOptional: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectClassName: PropTypes.string,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
};
